import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCart } from '../../../../hooks/useCart';

import { ROUTE_PATH } from '../../../../route/utils';

import { ReactComponent as CartIcon } from '../../../../icons/cart.svg';

import './menu-grid-item.scss';

import { Currency } from '../../../../utils/currency/Currency';
import { MACRO_DEFS } from '../../../../utils/macros';
import { getColorForKeyword } from '../../../../utils/tools';
import { Button } from '../../../common/button/Button';
import { MenuItemOptionsModal } from '../../../modal/menu-item-options/MenuItemOptionsModal';
import { QuantitySelector } from '../../../quantity-selector/QuantitySelector';
import { ExpandableText } from '../../../see-more/ExpandableText';

// Shorter than utils/macros' MACRO_NAMES — this card is a fixed 250px, too narrow for "Calories".
const MACRO_LABELS = { calories: 'Cal', protein: 'Protein', carbs: 'Carbs', fat: 'Fat' };

export const MenuGridItem = ({ item }) => {
  const { addToCart, cart, isItemInCart, incrementItem, decrementItem } = useCart();
  const navigate = useNavigate();
  const [showOptionsModal, setShowOptionsModal] = useState(false);

  const hasOptions = item.optionGroups?.length > 0;

  const isInCart = useMemo(() => {
    return isItemInCart(item);
    //eslint-disable-next-line
  }, [cart]);

  const cartItem = useMemo(() => {
    return cart.items.find((cartItem) => cartItem.id === item.id);
  }, [cart, item]);

  const macros = useMemo(() => {
    const values = {
      calories: item.nutrition?.caloriesKcal,
      protein: item.nutrition?.macros?.proteinG,
      carbs: item.nutrition?.macros?.carbsG,
      fat: item.nutrition?.macros?.fatG
    };
    return MACRO_DEFS.map((def) => ({
      ...def,
      label: def.key === 'calories' ? '' : def.label,
      value: values[def.key]
    })).filter(({ value }) => value != null);
  }, [item]);

  const getServingsText = (item) => {
    const servings = item.nutrition?.servings;

    if (!servings) {
      return null;
    }

    return servings > 1 ? `${servings} servings` : 'Single Serving';
  };

  const getWeightText = (item) => {
    const weight = item.nutrition?.weight;

    if (!weight) {
      return null;
    }

    return `Total weight ${weight}g`;
  };

  const getNutritionText = (item) => {
    const servings = getServingsText(item);
    const weight = getWeightText(item);

    if (!servings && !weight) {
      return 'Nutrition info not provided';
    }

    if (!servings) {
      return weight;
    } else if (!weight) {
      return servings;
    }

    return `${servings} | ${weight}`;
  };

  const navigateToItem = (id) => {
    navigate(`${ROUTE_PATH.MENU_ITEM}/${id}`);
  };

  return (
    <div className="menu-grid-item">
      <div className="menu-grid-item__image">
        <img
          className="menu-grid-item__image-primary"
          src={item.imageUrl}
          alt={`Menu Grid Item ${item.id}`}
        />
        {item.secondaryImageUrl && (
          <img className="menu-grid-item__image-secondary" src={item.secondaryImageUrl} alt="" />
        )}
        <div className="menu-grid-item__scrim" />
        <div className="menu-grid-item__categories">
          {item.categories.map((category) => (
            <span
              key={category.id ?? category.name}
              className={`menu-grid-item__tag ${!category.color ? getColorForKeyword(category.name, 'menu-grid-item__tag') : ''}`}
              style={category.color ? { backgroundColor: category.color } : undefined}
            >
              {category.name}
            </span>
          ))}
        </div>
        {getWeightText(item) && (
          <span className="menu-grid-item__weight">{item.nutrition.weight} g</span>
        )}
      </div>
      <div className="menu-grid-item__content" onClick={() => navigateToItem(item.id)}>
        <div className="menu-grid-item__head">
          <span className="menu-grid-item__name">{item.name}</span>
          <Currency className="menu-grid-item__price" value={item.price} />
        </div>
        <div className="menu-grid-item__desc-wrap">
          <span className="menu-grid-item__description">
            <ExpandableText text={item.description} maxChars={80} />
          </span>
        </div>
        {macros.length > 0 ? (
          <div className="menu-grid-item__macros">
            {macros.map(({ key, label, color, value }) => (
              <div key={key} className="menu-grid-item__macro-pill">
                <span style={{ color }}>
                  {value}
                  {label}
                </span>
                <small>{MACRO_LABELS[key]}</small>
              </div>
            ))}
          </div>
        ) : (
          <div className="menu-grid-item__macros menu-grid-item__macros--empty">
            Nutrition on pack
          </div>
        )}
        <div>
          <span className="menu-grid-item__nutrition">{getNutritionText(item)}</span>
        </div>
        {(!isInCart || hasOptions) && (
          <div className="menu-grid-item__quantity">
            <Button
              className="menu-grid-item__action-button"
              onClick={(e) => {
                e.stopPropagation();
                if (hasOptions) {
                  setShowOptionsModal(true);
                } else {
                  addToCart(item);
                }
              }}
            >
              <CartIcon width={24} height={24} />
              <span>ADD TO CART</span>
            </Button>
          </div>
        )}
        {isInCart && !hasOptions && (
          <div className="menu-grid-item__quantity">
            <QuantitySelector
              fullWidth
              quantity={cartItem.quantity}
              onIncrement={(e) => {
                e?.stopPropagation?.();
                incrementItem(item);
              }}
              onDecrement={(e) => {
                e?.stopPropagation?.();
                decrementItem(item);
              }}
            />
          </div>
        )}
      </div>
      {hasOptions && (
        <MenuItemOptionsModal
          open={showOptionsModal}
          onOpen={setShowOptionsModal}
          item={item}
          onConfirm={({ selectedOptionIds, selectedOptions, priceDelta }) => {
            addToCart({
              ...item,
              basePrice: item.price,
              price: item.price + priceDelta,
              selectedOptionIds,
              selectedOptions
            });
          }}
        />
      )}
    </div>
  );
};
